import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Award, Download, Search } from "lucide-react";

export default function Analytics() {
  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Analytics</h1>
      </div>
      <Tabs defaultValue="course">
        <TabsList>
          <TabsTrigger value="course" data-testid="analytics-tab-course">Courses</TabsTrigger>
          <TabsTrigger value="class" data-testid="analytics-tab-class">Classes</TabsTrigger>
          <TabsTrigger value="student" data-testid="analytics-tab-student">Students</TabsTrigger>
          <TabsTrigger value="card" data-testid="analytics-tab-card">Cards</TabsTrigger>
          <TabsTrigger value="export" data-testid="analytics-tab-export">Export</TabsTrigger>
        </TabsList>
        <TabsContent value="course"><CourseAnalytics /></TabsContent>
        <TabsContent value="class"><ClassAnalytics /></TabsContent>
        <TabsContent value="student"><StudentAnalytics /></TabsContent>
        <TabsContent value="card"><CardAnalytics /></TabsContent>
        <TabsContent value="export"><ExportAnalytics /></TabsContent>
      </Tabs>
    </div>
  );
}

function ExportAnalytics() {
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const { data: classesData } = useQuery({ queryKey: ["admin-classes"], queryFn: async () => (await api.get("/admin/students/classes")).data });
  const classes = classesData?.classes ?? [];
  const { data } = useQuery({
    queryKey: ["export-students", q, classFilter],
    queryFn: async () => (await api.get(`/admin/students?limit=100000&q=${encodeURIComponent(q)}${classFilter ? `&class_name=${encodeURIComponent(classFilter)}` : ""}`)).data,
  });
  const students = data?.students ?? [];
  const allIds = students.map((s) => s.id);
  const allSelected = allIds.length > 0 && selected.length === allIds.length;
  const toggleAll = () => setSelected(allSelected ? [] : allIds);
  const toggleOne = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/analytics/export",
        { student_ids: selected, class_name: selected.length ? null : (classFilter || null) },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `science-sorted-students-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selected.length || students.length} student${(selected.length || students.length) === 1 ? "" : "s"}`);
    } catch (e) { toast.error("Export failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-xl">
          Tick the students you want, then download an Excel overview of their activity. Leave all unticked to export everyone {classFilter ? "in this class" : "shown"}.
        </p>
        <Button onClick={download} disabled={busy || students.length === 0} data-testid="export-download-button" className="bg-slate-900 hover:bg-slate-800">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          Download Excel {selected.length ? `(${selected.length})` : `(all ${students.length})`}
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students…" className="pl-9 bg-white" data-testid="export-search-input" />
        </div>
        {classes.length > 0 && (
          <Select value={classFilter || "__all__"} onValueChange={(v) => { setClassFilter(v === "__all__" ? "" : v); setSelected([]); }}>
            <SelectTrigger className="w-56 bg-white" data-testid="export-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All classes</SelectItem>
              {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-2.5 bg-slate-50">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="export-select-all" aria-label="Select all" />
          <span className="text-sm font-medium text-slate-600">{selected.length} of {students.length} selected</span>
        </div>
        <div className="max-h-[420px] overflow-auto divide-y divide-slate-100">
          {students.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">No students match.</div>
          ) : students.map((s) => (
            <label key={s.id} className="flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-50" data-testid={`export-student-${s.id}`}>
              <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggleOne(s.id)} data-testid={`export-check-${s.id}`} />
              <span className="font-medium text-slate-800 flex-1">{s.name}</span>
              <span className="text-slate-400">{s.class_name || "—"}</span>
              <span className="text-slate-400 w-32 truncate text-right">{s.username}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function CourseAnalytics() {
  const [cid, setCid] = useState("");
  const { data: coursesData } = useQuery({ queryKey: ["admin-courses"], queryFn: async () => (await api.get("/admin/courses")).data });
  const { data, isLoading } = useQuery({ queryKey: ["course-analytics", cid], enabled: !!cid, queryFn: async () => (await api.get(`/admin/analytics/courses/${cid}`)).data });

  return (
    <div className="mt-4 space-y-4">
      <Picker value={cid} onChange={setCid} items={coursesData?.courses} placeholder="Select a course" testid="analytics-course-select" />
      {cid && (isLoading ? <Spin /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Metric value={data.students} label="Students" />
            <Metric value={`${data.avg_recall}%`} label="Avg recall (7d)" />
            <Metric value={data.reviews_week} label="Reviews (7d)" />
          </div>
          <WeakList items={data.weakest_topics} />
        </div>
      ))}
    </div>
  );
}

function ClassAnalytics() {
  const [cls, setCls] = useState("");
  const { data: classesData } = useQuery({ queryKey: ["admin-classes"], queryFn: async () => (await api.get("/admin/students/classes")).data });
  const classes = (classesData?.classes ?? []).map((c) => ({ id: c, name: c }));
  const { data, isLoading } = useQuery({ queryKey: ["class-analytics", cls], enabled: !!cls, queryFn: async () => (await api.get(`/admin/analytics/classes/${encodeURIComponent(cls)}`)).data });

  return (
    <div className="mt-4 space-y-4">
      {classes.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="no-classes">
          No classes yet. Add a class to your students (on Add student, Edit, or the bulk import "Class" column) to see class analytics.
        </div>
      ) : (
        <Picker value={cls} onChange={setCls} items={classes} placeholder="Select a class" testid="analytics-class-select" />
      )}
      {cls && (isLoading ? <Spin /> : (
        <div className="space-y-4" data-testid="class-analytics-panel">
          <h3 className="font-display text-lg font-semibold text-slate-900">{cls}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric value={data.students} label="Students" />
            <Metric value={data.active_students} label="Active (7d)" />
            <Metric value={`${data.avg_recall}%`} label="Avg recall (7d)" />
            <Metric value={data.reviews_week} label="Reviews (7d)" />
          </div>
          <WeakList items={data.weakest_topics} />
        </div>
      ))}
    </div>
  );
}

function StudentAnalytics() {
  const [q, setQ] = useState("");
  const [sid, setSid] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const { data: studentsData } = useQuery({
    queryKey: ["admin-students-analytics", q],
    queryFn: async () => (await api.get(`/admin/students?limit=25&q=${encodeURIComponent(q)}`)).data,
  });
  const { data, isLoading } = useQuery({ queryKey: ["student-analytics", sid], enabled: !!sid, queryFn: async () => (await api.get(`/admin/analytics/students/${sid}`)).data });
  const students = studentsData?.students ?? [];

  return (
    <div className="mt-4 space-y-4">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students by name, username or email…" data-testid="analytics-student-search"
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
      <div className="space-y-1 max-w-md" data-testid="analytics-student-results">
        {students.length === 0 ? (
          <div className="text-sm text-slate-400 px-1">No students match your search.</div>
        ) : students.map((s) => (
          <button key={s.id} onClick={() => { setSid(s.id); setSelectedName(s.name); }} data-testid={`analytics-student-${s.id}`}
            className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${sid === s.id ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-700 bg-white border border-slate-200"}`}>
            <span className="font-medium">{s.name}</span>
            <span className={sid === s.id ? "text-slate-300 ml-2" : "text-slate-400 ml-2"}>{s.username} · {s.email}</span>
          </button>
        ))}
      </div>
      {sid && (isLoading ? <Spin /> : (
        <div className="space-y-4" data-testid="student-analytics-panel">
          <h3 className="font-display text-lg font-semibold text-slate-900">{data.name || selectedName}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric value={`${data.retention}%`} label="Retention (7d)" />
            <Metric value={data.reviews_week} label="Reviews (7d)" />
            <Metric value={data.cards_learned} label="Cards learned" />
            <Metric value={data.cards_due} label="Cards due" />
          </div>
          <div className="text-sm text-slate-500">Courses: {data.courses.join(", ") || "none"} · Study days this week: {data.study_days} · Last study: {data.last_study ? new Date(data.last_study).toLocaleString("en-GB") : "never"}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <WeakList items={data.weak_topics} title="Weakest topics" />
            <div>
              <h3 className="font-display font-semibold text-slate-900 mb-2 flex items-center gap-2"><Award className="h-4 w-4 text-emerald-500" /> Strongest topics</h3>
              {data.strong_topics.length === 0 ? <Empty /> : data.strong_topics.map((t) => (
                <Row key={t.topic} name={t.topic} value={`${t.recall}%`} tone="emerald" />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CardAnalytics() {
  const [q, setQ] = useState("");
  const { data: cardsData } = useQuery({ queryKey: ["cards-for-analytics", q], queryFn: async () => (await api.get(`/admin/cards?q=${encodeURIComponent(q)}&limit=15`)).data });
  const [cardId, setCardId] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["card-analytics", cardId], enabled: !!cardId, queryFn: async () => (await api.get(`/admin/analytics/cards/${cardId}`)).data });

  return (
    <div className="mt-4 space-y-4">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cards…" data-testid="analytics-card-search"
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
      <div className="space-y-1">
        {(cardsData?.cards ?? []).map((c) => (
          <button key={c.id} onClick={() => setCardId(c.id)} data-testid={`analytics-card-${c.id}`}
            className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${cardId === c.id ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-700 bg-white border border-slate-200"}`}>
            {c.question.slice(0, 90)}
          </button>
        ))}
      </div>
      {cardId && (isLoading ? <Spin /> : (
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="card-analytics-panel">
          <div className="text-slate-800">{data.question}</div>
          <div className="text-sm text-slate-500">{data.reviewers} students · {data.total_reviews} reviews · <span className="font-medium text-slate-900">{data.recall}% recall</span></div>
          <div className="grid grid-cols-4 gap-2">
            {["again", "hard", "good", "easy"].map((k) => (
              <div key={k} className="rounded-lg border border-slate-200 p-3 text-center">
                <div className="font-display text-xl font-bold text-slate-900">{data.pct[k]}%</div>
                <div className="text-xs text-slate-500 capitalize">{k}</div>
              </div>
            ))}
          </div>
          {data.pct.again >= 50 && data.total_reviews >= 10 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> High Again rate — this card may be difficult or need rewriting.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const Spin = () => <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
const Empty = () => <div className="text-sm text-slate-400">Not enough data yet.</div>;

function Picker({ value, onChange, items, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="max-w-md bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{(items ?? []).map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function Metric({ value, label }) {
  return <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="font-display text-2xl font-bold text-slate-900">{value}</div><div className="text-xs text-slate-500 mt-0.5">{label}</div></div>;
}
function Row({ name, value, tone }) {
  const c = tone === "emerald" ? "text-emerald-600" : "text-amber-600";
  return <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5 mb-2"><span className="text-sm text-slate-700">{name}</span><span className={`font-mono text-sm ${c}`}>{value}</span></div>;
}
function WeakList({ items, title = "Weakest topics" }) {
  return (
    <div>
      <h3 className="font-display font-semibold text-slate-900 mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> {title}</h3>
      {(!items || items.length === 0) ? <Empty /> : items.map((t) => <Row key={t.topic} name={t.topic} value={`${t.recall}%`} tone="amber" />)}
    </div>
  );
}
